import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download, Smartphone, Check } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const InstallAppModal = () => {
  const [open, setOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone) { setIsInstalled(true); return; }
    const handler = (e: Event) => { e.preventDefault(); setDeferredPrompt(e as BeforeInstallPromptEvent); };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => { setIsInstalled(true); setDeferredPrompt(null); });
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleTryInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setIsInstalled(true);
    setDeferredPrompt(null);
  };

  if (isInstalled) {
    return (<Button variant="secondary" size="sm" disabled className="gap-2 opacity-70"><Check className="h-4 w-4" />{t('install.appInstalled')}</Button>);
  }

  return (
    <>
      <Button onClick={() => setOpen(true)} size="sm" className="gap-2"><Download className="h-4 w-4" />{t('install.installApp')}</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl"><Smartphone className="h-5 w-5 text-primary" />{t('install.installTheApp')}</DialogTitle>
            <DialogDescription>{t('install.nativeExperience')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div>
              <h3 className="font-semibold text-sm text-foreground mb-2">{t('install.whyInstall')}</h3>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                <li>✅ {t('install.fasterAccess')}</li>
                <li>✅ {t('install.fullScreen')}</li>
                <li>✅ {t('install.feelsLikeApp')}</li>
              </ul>
            </div>
            <div className="rounded-lg border bg-muted/30 p-3">
              <h3 className="font-semibold text-sm text-foreground mb-2">{t('install.iphoneTitle')}</h3>
              <ol className="space-y-1 text-sm text-muted-foreground list-decimal list-inside">
                <li>{t('install.iphoneStep1')}</li>
                <li>{t('install.iphoneStep2')}</li>
                <li>{t('install.iphoneStep3')}</li>
                <li>{t('install.iphoneStep4')}</li>
              </ol>
            </div>
            <div className="rounded-lg border bg-muted/30 p-3">
              <h3 className="font-semibold text-sm text-foreground mb-2">{t('install.androidTitle')}</h3>
              <ol className="space-y-1 text-sm text-muted-foreground list-decimal list-inside">
                <li>{t('install.androidStep1')}</li>
                <li>{t('install.androidStep2')}</li>
                <li>{t('install.androidStep3')}</li>
                <li>{t('install.androidStep4')}</li>
              </ol>
            </div>
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            {deferredPrompt && <Button onClick={handleTryInstall} className="w-full sm:w-auto">{t('install.tryInstall')}</Button>}
            <Button variant="outline" onClick={() => setOpen(false)} className="w-full sm:w-auto">{t('install.close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InstallAppModal;
